import { useApolloClient } from '@apollo/client/react'
import { Box, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Button } from 'settle-ui/components/Button/Button'
import { Modal } from 'settle-ui/components/Modal/Modal'
import { TextFieldController } from 'settle-ui/form/TextFieldController/TextFieldController'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { logEvent } from 'lib/tracking/segment'
import { useAppliedPromoCodeContext } from 'routes/hooks/useAppliedPromoCodeContext/useAppliedPromoCodeContext'
import { ApplyPromoCodeDialog_verifyDocument } from './ApplyPromoCodeDialog.graphql'
import * as events from './ApplyPromoCodeDialog.events'

interface ApplyPromoCodeDialogProps {
  open: boolean
  promoCode?: string
  onClose: () => void
  onSuccess: () => void
}

interface ApplyPromoCodeFormValues {
  promoCode: string
}

export const ApplyPromoCodeDialog: Settle.FC<ApplyPromoCodeDialogProps> = ({
  open,
  onClose,
  onSuccess,
  promoCode = '',
}) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const { setPromoCode } = useAppliedPromoCodeContext()
  const { showApolloOrTextError, showError } = useSnackbars()
  const form = useForm<ApplyPromoCodeFormValues>({
    defaultValues: {
      promoCode,
    },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      setLoading(true)
      void logEvent(events.NEXT, { code: values.promoCode })
      const { data } = await client.query({
        errorPolicy: 'none',
        query: ApplyPromoCodeDialog_verifyDocument,
        variables: { code: values.promoCode },
      })
      const { promotionCodes = [] } = data

      if (promotionCodes.length === 0) {
        form.setError('promoCode', { message: '' })
        return showError(t('applyPromoCodeDialog::validation::invalid'))
      }

      const activePromoCode = promotionCodes.find((promoCode) => promoCode.active)

      if (!activePromoCode) {
        form.setError('promoCode', { message: '' })
        return showError(t('applyPromoCodeDialog::validation::expired'))
      }

      setPromoCode({ ...activePromoCode.coupon, id: activePromoCode.id, applied: false })
      return onSuccess()
    } catch (err) {
      return showApolloOrTextError(err)
    } finally {
      setLoading(false)
    }
  })

  const handleCancel = () => {
    void logEvent(events.CANCEL)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} data-testid="apply-promo-code-dialog">
      <Modal.Header title={t('applyPromoCodeDialog::title')} onClose={onClose} />
      <Modal.Content>
        <Box component="form" onSubmit={handleSubmit} id="apply-promo-code-form" noValidate={true}>
          <Typography color="textSecondary">{t('applyPromoCodeDialog::body')}</Typography>
          <TextFieldController
            name="promoCode"
            control={form.control}
            label={t('applyPromoCodeDialog::textFieldLabel')}
            rules={{ required: { value: true, message: t('validation::cantBeEmpty') } }}
          />
        </Box>
      </Modal.Content>
      <Modal.Footer
        rightButtons={
          <>
            <Button onClick={handleCancel}>{t('cancel')}</Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              form="apply-promo-code-form"
              data-testid="apply-promo-code-submit-button"
            >
              {t('next')}
            </Button>
          </>
        }
      />
    </Modal>
  )
}
